import { useState } from 'react'
import {
  createSunoPrompt,
  deleteSunoPrompt,
  suggestSunoPromptMetadata,
  updateSunoPrompt,
  type SunoPrompt
} from '../../../api/suno'
import type { PromptEditorState } from '../PromptEditorDialog'

type PromptLibraryEditorActionsOptions = {
  onPromptGroupsChanged: () => Promise<void>
  onPromptsChanged: () => Promise<void>
  toast: (message: string) => void
}

type PromptMetadataInput = Pick<PromptEditorState, 'excludePrompt' | 'stylePrompt'>

export const usePromptLibraryEditorActions = ({
  onPromptGroupsChanged,
  onPromptsChanged,
  toast
}: PromptLibraryEditorActionsOptions) => {
  const [promptSaving, setPromptSaving] = useState(false)
  const [promptDeleting, setPromptDeleting] = useState(false)

  const savePrompt = async (input: PromptEditorState): Promise<boolean> => {
    const title = input.title.trim()
    const style = input.stylePrompt.trim()

    if (!title || !style) {
      return false
    }

    setPromptSaving(true)
    try {
      const payload = {
        title,
        description: input.description.trim() || null,
        sunoGroupIds: input.groupIds,
        style_prompt: style,
        exclude_prompt: input.excludePrompt.trim() || null
      }

      if (input.id) {
        await updateSunoPrompt({
          promptId: input.id,
          ...payload
        })
        toast(`Updated prompt "${title}".`)
      } else {
        await createSunoPrompt(payload)
        toast(`Saved prompt "${title}".`)
      }

      await onPromptsChanged()
      await onPromptGroupsChanged()
      return true
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to save prompt.')
      return false
    } finally {
      setPromptSaving(false)
    }
  }

  const confirmDeletePrompt = async (prompt: SunoPrompt): Promise<boolean> => {
    setPromptDeleting(true)
    try {
      await deleteSunoPrompt(prompt.id)
      toast(`Deleted prompt "${prompt.title}".`)
      await onPromptsChanged()
      return true
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Unable to delete prompt.')
      return false
    } finally {
      setPromptDeleting(false)
    }
  }

  const suggestPromptMetadata = async (input: PromptMetadataInput): Promise<{ title: string; description: string }> => {
    const result = await suggestSunoPromptMetadata({
      style_prompt: input.stylePrompt,
      exclude_prompt: input.excludePrompt || null
    })

    return {
      title: result.title,
      description: result.description
    }
  }

  return {
    promptDeleting,
    promptSaving,
    confirmDeletePrompt,
    savePrompt,
    suggestPromptMetadata
  }
}
